import { IDropdownOption } from 'office-ui-fabric-react';
import { SortBy } from 'types/common';
import { emptyArray } from './array';
import { FilterModalItem } from '../types/filters';

export const asDropdownOptions = (
  items: {
    [key: string]: string;
    [key: number]: string;
  },
  keyToInt?: boolean
): IDropdownOption[] =>
  Object.keys(items).map((key) => ({
    key: keyToInt && !Number.isNaN(Number(key)) ? Number(key) : key,
    text: items[key],
  }));

export const toDropdownOptions = ({
  items,
  textKey = 'name',
  uniqueKey = 'id',
  excludeDuplicates,
}: {
  items: Record<string, unknown>[];
  uniqueKey?: string | number;
  textKey?: string | number;
  excludeDuplicates?: boolean;
}): IDropdownOption[] => {
  return items.reduce((res, item) => {
    if (excludeDuplicates && res.find(({ text }) => text === item[textKey]))
      return res;
    return [
      ...res,
      {
        key: item[uniqueKey],
        text: item[textKey],
      } as IDropdownOption,
    ];
  }, [] as IDropdownOption[]);
};

export const toSearchParams = (obj: { [key: string]: any }) =>
  new window.URLSearchParams(obj);

export const sortToSubMenuItems = ({
  sort,
  selected,
  handleClick,
}: {
  sort: SortBy[];
  handleClick: (sort: string) => void;
  selected: string;
}) =>
  sort.reduce(
    (res, item) => [
      ...res,
      ...emptyArray(2).map((_, i) => {
        const key = `${item.key} ${i ? 'asc' : 'desc'}`;
        const name = `${item.name}, ${i ? 'по возрастанию' : 'по убыванию'}`;
        return {
          key,
          name,
          onClick: () => handleClick(key),
          iconProps: {
            iconName: key === selected ? 'Accept' : '',
          },
        };
      }),
    ],
    [] as SortBy[]
  );

export const convertPrimitivesArrayToDropdownOptions = (
  primitives: (string | number)[]
): IDropdownOption[] =>
  primitives.map((value) => ({
    key: value,
    text: `${value}`,
  }));

export const convertPrimitivesArrayToFiltersModalItem = (
  items: (string | number)[],
  nullReplacer: string = ' - ',
): FilterModalItem[] => {
  return items.map((item) => ({
    key: item || `${item}`,
    primaryField: item ? `${item}` : nullReplacer,
  }));
};

export const toFilterModalItemsAdas = <T extends Record<string, any>>({
  items,
  key,
  primaryField,
  secondaryField = 'name'
}: {
  items: T[];
  primaryField: keyof T;
  key: keyof T;
  secondaryField?: any;
}) =>
  items.map((item, index) => ({
    ...item,
    primaryField: item[primaryField] as string || item[secondaryField] as string,
    key: `${item[key]}_${index}` as string,
  }));

export const toFilterModalItems = <T extends Record<string, unknown>>({
  items,
  key,
  primaryField,
}: {
  items: T[];
  primaryField: keyof T;
  key: keyof T;
}): FilterModalItem[] =>
  items.map((item) => ({
    ...item,
    primaryField: item[primaryField] as string,
    key: item[key] as string,
  }));

export const deleteEmptyItems = (data: { [key: string]: any[] }) => {
  const res = { ...data };
  Object.keys(res).forEach((key) => {
    if (!res[key].length) delete res[key];
  });
  return res;
};

export const createDictionary = (
  items: [string, string][]
): [{ [key: string]: string }, { [key: string]: string }] => {
  const res: [{ [key: string]: string }, { [key: string]: string }] = [{}, {}];
  items.forEach(([key1, key2]) => {
    res[0][key1] = key2;
    res[1][key2] = key1;
  });
  return res;
};

export const renameSignatures = (
  items: { [key: string]: any },
  dictionary: { [key: string]: string }
) => {
  const res = { ...items };
  Object.keys(items).forEach((key) => {
    if (key !== dictionary[key]) {
      res[dictionary[key]] = items[key];
      delete res[key];
    }
  });
  return res;
};
